/**
 * Faixa da aba de depuração (spec 020, FR-008): fixa no topo de TODA tela da
 * aba e sem botão de fechar — "Vendo como Maria (Cliente X) · motivo: … ·
 * expira em 58:12 · Encerrar". Quando a contagem chega a zero a aba acaba
 * sozinha; Encerrar invalida no servidor (FR-009) antes de acabar aqui.
 */
import { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import type { ImpersonationCurrent } from '@erp/shared';
import { api } from '@/lib/api-client';
import { endImpersonationLocally } from '@/auth/impersonation';
import { Button } from '@/components/ui/button';

function secondsLeft(expiresAt: string | Date): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

/** 3599 → "59:59" */
export function formatCountdown(total: number): string {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function useSecondsLeft(expiresAt: string | Date): number {
  const [left, setLeft] = useState(() => secondsLeft(expiresAt));
  useEffect(() => {
    const id = window.setInterval(() => setLeft(secondsLeft(expiresAt)), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  return left;
}

const Dot = () => (
  <span aria-hidden className="text-[#D48806]">
    ·
  </span>
);

export function ImpersonationBanner({ session }: { session: ImpersonationCurrent }) {
  const left = useSecondsLeft(session.expiresAt);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (left <= 0) endImpersonationLocally('expired');
  }, [left]);

  const end = async () => {
    setEnding(true);
    setError(null);
    const res = await api<void>('/impersonation/end', { method: 'POST' });
    // 204, ou 401 porque ela já não valia: nos dois casos a sessão acabou
    if (!res.error || res.error.status === 401) {
      endImpersonationLocally('ended');
      return;
    }
    setEnding(false);
    setError('Não foi possível encerrar agora. Tente de novo.');
  };

  const clients = session.target.clients.join(', ');

  return (
    <div
      role="region"
      aria-label="Sessão de depuração"
      className="sticky top-0 z-50 border-b border-[#FFE58F] bg-[#FFFBE6] px-4 py-2 text-[13px] text-[#613400] shadow-xs"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Eye className="size-4 shrink-0 text-[#D48806]" aria-hidden />
        <span className="font-medium">
          Vendo como <strong>{session.target.name}</strong>
          {clients ? ` (${clients})` : ''}
        </span>
        <Dot />
        <span className="max-w-[28rem] min-w-0 truncate" title={session.reason}>
          motivo: {session.reason}
        </span>
        <Dot />
        <span>
          expira em{' '}
          <time className="font-semibold tabular-nums" aria-live="off">
            {formatCountdown(left)}
          </time>
        </span>
        <Dot />
        <span>só leitura</span>
        <div className="ml-auto flex items-center gap-2">
          {error ? (
            <span className="text-destructive" role="alert">
              {error}
            </span>
          ) : null}
          <Button
            size="xs"
            variant="outline"
            className="border-[#D48806] bg-white text-[#874D00] hover:bg-[#FFF1B8]"
            onClick={() => void end()}
            disabled={ending}
          >
            {ending ? 'Encerrando…' : 'Encerrar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
